import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../components/layout/DashboardShell";
import { Card, Button } from "../components/common/Primitives";
import api, { formatApiError } from "../lib/api";
import { Mail, Save, Lock } from "lucide-react";

export default function NotificationPreferences() {
  const [categories, setCategories] = useState([]);
  const [unsubscribe, setUnsubscribe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/notifications/preferences");
        setCategories(r.data?.categories || []);
        setUnsubscribe(r.data?.unsubscribe || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const isSubscribed = (id) => !unsubscribe.includes(id);
  const toggle = (id) => {
    if (unsubscribe.includes(id)) {
      setUnsubscribe(unsubscribe.filter((c) => c !== id));
    } else {
      setUnsubscribe([...unsubscribe, id]);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/notifications/preferences", { unsubscribe });
      toast.success("Preferences saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-8">
        <div className="overline">SETTINGS</div>
        <h1 className="font-display text-3xl lg:text-4xl mt-1">Email preferences</h1>
        <div className="text-sm text-ink-soft mt-1 mb-6">
          Choose which VenturaZ emails you&apos;d like to receive. Transactional emails (password reset, KYC decisions,
          e-sign links) are always sent and cannot be turned off.
        </div>

        <Card testId="email-prefs-card">
          {loading ? (
            <div className="text-sm text-ink-muted italic py-6">Loading preferences…</div>
          ) : (
            <div className="space-y-3">
              {categories.map((c) => {
                const on = isSubscribed(c.id);
                const locked = c.required;
                return (
                  <div key={c.id}
                    className={`border rounded-md p-4 flex items-start justify-between gap-3 ${locked ? "border-navy/10 bg-navy/[0.02]" : "border-navy/10"}`}
                    data-testid={`pref-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Mail size={14} className="text-emerald-800" />
                        <div className="font-heading text-ink">{c.label}</div>
                        {locked && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                            <Lock size={10} /> Always on
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-ink-soft">{c.description}</div>
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs shrink-0">
                      <input type="checkbox" checked={on} disabled={locked}
                        onChange={() => toggle(c.id)}
                        data-testid={`pref-toggle-${c.id}`} />
                      Subscribed
                    </label>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <Button variant="gold" onClick={save} disabled={busy || loading} data-testid="pref-save">
              <Save size={14} className="inline mr-1" /> {busy ? "Saving…" : "Save preferences"}
            </Button>
          </div>
        </Card>
      </div>
    </DashboardShell>
  );
}
